import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormArray, FormControl, Validators, AbstractControl } from '@angular/forms';
import { BudgetService } from 'src/app/data-editor/services/budget.service';
import { Budget } from 'src/app/data-editor/models/models';
import { BudgetItem } from 'src/app/models/budget-item';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'lsz-budget-edit',
  templateUrl: './budget-edit.component.html',
  styleUrls: ['./budget-edit.component.scss'],
})
export class BudgetEditComponent implements OnInit, OnDestroy {
  public faInfoCircle: typeof faInfoCircle;
  public title: string;
  public budgets: Budget[] = [];
  public budgetForm: FormGroup;
  public isEditing = false;
  public isSaving = false;
  public readonly minValue = 1;
  public readonly maxValue = 1000000000;

  private onDestroy$ = new Subject<void>();

  constructor(private budgetService: BudgetService) {
    this.budgetForm = new FormGroup({
      budgetsArray: new FormArray([]),
    });
    this.faInfoCircle = faInfoCircle;
  }

  public async ngOnInit(): Promise<void> {
    this.title = 'Budgets bearbeiten';
    await this.loadBudgets();
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private async loadBudgets(): Promise<void> {
    const budgets: Budget[] = await firstValueFrom(this.budgetService.get());
    // Sort budgets by id to ensure proper order
    this.budgets = budgets.sort((a, b) => {
      return Number(a.id) - Number(b.id);
    });
    this.initBudgetForm();
  }

  private initBudgetForm(): void {
    const budgetsArray = new FormArray([]);

    this.budgets.forEach((budget, index) => {
      const isLastBudget = index === this.budgets.length - 1;

      const formGroup = new FormGroup({
        id: new FormControl(budget.id),
        name: new FormControl({ value: budget.name, disabled: true }),
        upperLimit: new FormControl({
          value: isLastBudget ? null : budget.upperLimit,
          disabled: isLastBudget,
        }),
      });
      // For the last budget, upperLimit should be null and readonly
      // For other budgets, add validation as before
      if (!isLastBudget) {
        this.setValidatorsForBudgetGroup(formGroup, budget.lowerLimit);
      }

      // Listen to upperLimit changes in current budget to update validators on next budget
      if (index < this.budgets.length - 1) {
        formGroup
          .get('upperLimit')
          .valueChanges.pipe(takeUntil(this.onDestroy$))
          .subscribe((upperLimit) => {
            this.updateNextBudgetValidation(index, upperLimit);
          });
      }

      budgetsArray.push(formGroup);
    });

    this.budgetForm = new FormGroup({
      budgetsArray,
    });
  }

  // Update the min validator for the next budget's upperLimit when the current one changes
  private updateNextBudgetValidation(index: number, currentUpperLimit: number): void {
    if (index < this.budgetsFormArray.length - 1) {
      const nextBudget = this.budgetsFormArray.at(index + 1);
      this.setValidatorsForBudgetGroup(nextBudget, currentUpperLimit);
    }
  }
  private setValidatorsForBudgetGroup(budgetGroup: AbstractControl, upperLimit: number) {
    const minValueForValidator = Math.max(upperLimit + 1, this.minValue);
    budgetGroup
      .get('upperLimit')
      .setValidators([Validators.required, Validators.min(minValueForValidator), Validators.max(this.maxValue)]);
    budgetGroup.get('upperLimit').updateValueAndValidity();
  }

  get budgetsFormArray(): FormArray {
    return this.budgetForm.get('budgetsArray') as FormArray;
  }

  public getUpperLimitFromPreviousOrNull(index: number): number | null {
    if (index === 0) {
      // First budget's lowerLimit is null
      return null;
    } else {
      const prevBudgetGroup = this.budgetsFormArray.at(index - 1);
      return prevBudgetGroup.get('upperLimit').value;
    }
  }

  public toggleEditing(): void {
    this.isEditing = !this.isEditing;
    if (!this.isEditing) {
      // Reset form if cancelling
      this.initBudgetForm();
    }
  }

  public save(): void {
    if (this.budgetForm.valid) {
      this.isSaving = true;

      const budgetItem = this.budgetsFormArray.value as BudgetItem[];

      const updatedBudgets: Budget[] = budgetItem.map((item, index) => {
        const isFirstBudget = index === 0;
        const isLastBudget = index === budgetItem.length - 1;

        // Get original budget with all properties
        return {
          ...this.budgets.find((b) => b.id === item.id),
          name: item.name,
          // First budget has null lowerLimit
          lowerLimit: isFirstBudget ? null : this.getUpperLimitFromPreviousOrNull(index),
          // Last budget has null upperLimit
          upperLimit: isLastBudget ? null : item.upperLimit,
        };
      });

      // Call the bulk update method and reload budgets after save
      this.budgetService
        .updateBulk(updatedBudgets)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.isSaving = false;
            this.isEditing = false;
            this.loadBudgets();
          },
          error: () => {
            this.isSaving = false;
          },
        });
    }
  }
}
